import { Col, Row, Tag, Tooltip } from "antd";
import { ConsentTag } from "components/consentTag";
import { COMMUNICATION_TASK_STATUS, DATE_TIME_FORMAT } from "common/constants";
import { CursorTable } from "components/cursorTable";
import {
  useGetCommunicationTasksListQuery,
  useGetCommunicationHistoriesListQuery,
} from "features/communicationTasks/communicationTasksAPI";
import { useFetchCommunicationResultItemsQuery } from "features/communicationResultItems/communicationResultItemsAPI";
import NotesModal from "features/communicationsTable/components/notesModal";
import { useState, useMemo, useEffect, useRef } from "react";
import CommunicationTasksActions from "pages/communications/communicationTaskActions";
import styled from "styled-components";
import { CommunicationTasksBulkActions } from "pages/communications/communicationTaskBulkActions";
import { useFetchCommunicationMethodsQuery } from "features/communicationMethodsTable/communicationMethodsAPI";
import { formatDate, idToObjectMap } from "common/utils";
import { InfoCircleOutlined } from "@ant-design/icons";
import { PERMISSIONS, useAuthorization } from "features/permissions";
import { useFetchMeQuery } from "features/auth/authAPI";
import moment from "moment-timezone";

const StyledTable = styled(CursorTable)`
  & .ant-checkbox-input {
    pointer-events: auto;
  }

  & .ant-table-selection-column {
    pointer-events: none;
  }

  & .ant-table-container {
    max-height: ${({ $hideActionContainer }) =>
      $hideActionContainer ? "calc(100vh - 191px)" : "calc(100vh - 240px)"};
    overflow: scroll;
  }

  & .ant-table-cell {
    padding: 4px 8px !important;
  }

  & .ant-table-content > table,
  & .ant-table-body > table {
    min-width: 100%;
  }
`;

const StyledCol = styled(Col)`
  flex: 1;
  min-width: 0;
  overflow: auto;
`;

const StyledActionContainer = styled(Row)`
  background: #fff;
  height: 48px;
  margin-top: 1px;
  border-left: 1px solid #f0f0f0;
  align-items: center;
  flex: 1;
  min-width: 200px;
  display: flex;
  justify-content: space-between;
`;

const StyledActions = styled(Row)`
  margin-left: 47px;
  height: 100%;
  border-left: 1px solid #f0f0f0;
  align-items: center;
  justify-content: space-between;
`;

const StyledInfoCircleOutlined = styled(InfoCircleOutlined)`
  color: red;
  margin-left: 4px;
`;

const StyledTag = styled(Tag)`
  margin: 4px 4px;
`;

const StyledSpan = styled.span`
  white-space: pre-wrap;
`;

const StyledButton = styled.button`
  background: none;
  border: none;
  color: #1890ff;
  cursor: pointer;
  padding: 0;
`;

const gotoDebtorProfile = (record) => {
  const url = `/debtors/${record.debtorId}`;
  const newWindow = window.open(url, "_blank");
  if (newWindow) newWindow.opener = null;
};

function CommunicationTasksTable({ filterParams, hasAiPhoneAgentMedium, communicationTaskStatus }) {
  const [pagination, setPagination] = useState({
    virtualPage: 1,
    pageSize: 100,
    prevToken: null,
    nextToken: null,
  });
  const [selectedRows, setSelectedRows] = useState([]);

  const useHistoriesEndpoint =
    hasAiPhoneAgentMedium && communicationTaskStatus === COMMUNICATION_TASK_STATUS.SUCCESS;

  // Reset pagination when switching endpoints
  const prevUseHistoriesEndpoint = useRef(useHistoriesEndpoint);
  useEffect(() => {
    if (prevUseHistoriesEndpoint.current !== useHistoriesEndpoint) {
      setPagination({ virtualPage: 1, pageSize: 100, prevToken: null, nextToken: null });
      prevUseHistoriesEndpoint.current = useHistoriesEndpoint;
    }
  }, [useHistoriesEndpoint]);

  const { data: communicationMethods } = useFetchCommunicationMethodsQuery();
  const { data: allResultItems = [] } = useFetchCommunicationResultItemsQuery(
    { isEnabled: true },
    { skip: !useHistoriesEndpoint },
  );
  const { data: isCommunicationGetNotesAuthorized } = useAuthorization(
    PERMISSIONS.COMMUNICATION__GET_NOTES,
  );
  const { data: me } = useFetchMeQuery();
  const [selectedRecord, setSelectedRecord] = useState(null);

  const resultSlugToNameMap = useMemo(() => {
    return allResultItems.reduce((acc, item) => {
      acc[item.slug] = item.name;
      return acc;
    }, {});
  }, [allResultItems]);

  const communicationMethodIdToNameMap = useMemo(() => {
    if (!communicationMethods) return {};
    return idToObjectMap(communicationMethods);
  }, [communicationMethods]);

  const taskColumns = [
    {
      title: "Debtor ID",
      dataIndex: "debtorId",
      key: "debtorId",
      width: 100,
      render: (field) => field ?? "-",
    },
    {
      title: "Debtor Name",
      dataIndex: "debtorName",
      key: "debtorName",
      width: 100,
      render: (field) => field ?? "-",
    },
    {
      title: "Account ID(s)",
      dataIndex: "accountId",
      key: "accountId",
      width: 100,
      render: (text, record) => {
        if (record.accountIds && record.accountIds.length > 0) {
          return record.accountIds.join(", ");
        }
        return record.accountId ?? "-";
      },
    },
    {
      title: "Scheduled Date",
      dataIndex: "scheduledDate",
      key: "scheduledDate",
      width: 100,
      render: (text) => formatDate(text),
    },
    {
      title: "Method",
      dataIndex: "communicationMethodName",
      key: "communicationMethodName",
      width: 100,
      render: (text, record) => {
        const communicationMethod = communicationMethodIdToNameMap[record.communicationMethodId];
        if (!communicationMethod) return "-";
        return communicationMethod.name;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 100,
      key: "status",
      render: (text, record) => {
        if (text === "failed" && record.errorMessage !== null) {
          return (
            <Tooltip title={record.errorMessage} trigger="hover">
              {text.charAt(0).toUpperCase() + text.slice(1)}
              <span>
                <StyledInfoCircleOutlined />
              </span>
            </Tooltip>
          );
        }
        return text.charAt(0).toUpperCase() + text.slice(1);
      },
    },
    {
      title: "Actions",
      fixed: "right",
      align: "center",
      width: 50,
      dataIndex: "actions",
      render: (text, record) => <CommunicationTasksActions record={record} />,
    },
  ];

  const historiesColumns = [
    {
      title: "Timestamp",
      dataIndex: "communicationStartTime",
      key: "communicationStartTime",
      width: 200,
      render: (text, record) =>
        record.communicationStartTime
          ? moment(record.communicationStartTime).format(DATE_TIME_FORMAT)
          : "-",
    },
    {
      title: "Debtor",
      key: "debtor",
      width: 250,
      render: (_, record) => {
        const name = record.debtorName ?? "-";
        const id = record.debtorId ?? "-";
        return (
          <a
            href={`/debtors/${record.debtorId}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {name} ({id})
          </a>
        );
      },
    },
    {
      title: "Phone Number",
      key: "phoneNumber",
      width: 170,
      render: (_, record) => {
        const isInbound =
          record.isDebtorInitiated === true || record.communicationOrigin === "inbound";
        return (
          <span>
            {record.phoneNumber ?? "-"}
            {isInbound && " (Inbound)"}
          </span>
        );
      },
    },
    {
      title: "Results",
      width: 400,
      key: "Results",
      render: (_, communication) => {
        const callResults = communication.callResults ?? [];
        const mailResults = communication.mailResults ?? [];
        const textResults = communication.textResults ?? [];
        const communicationResults = communication.communicationResults ?? [];
        const voicemailResults = communication.voicemailResults ?? [];
        return (
          <div>
            {communicationResults.map((result) => (
              <StyledTag color="#2db7f5" key={result}>
                {resultSlugToNameMap[result] || result}
              </StyledTag>
            ))}
            {mailResults.map((result) => (
              <StyledTag color="#ebb25e" key={result}>
                {resultSlugToNameMap[result] || result}
              </StyledTag>
            ))}
            {callResults.map((result) => (
              <StyledTag color="#2fba5b" key={result}>
                {resultSlugToNameMap[result] || result}
              </StyledTag>
            ))}
            {voicemailResults.map((result) => (
              <StyledTag color="#2fba5b" key={result}>
                {resultSlugToNameMap[result] || result}
              </StyledTag>
            ))}
            {textResults.map((result) => {
              const display = resultSlugToNameMap[result];
              if (display) {
                return (
                  <StyledTag color="#2abcdc" key={result}>
                    {display}
                  </StyledTag>
                );
              }
              return null;
            })}
            {communication.isConsentForText != null && (
              <ConsentTag isConsent={communication.isConsentForText}>Text</ConsentTag>
            )}
            {communication.isConsentForEmail != null && (
              <ConsentTag isConsent={communication.isConsentForEmail}>Email</ConsentTag>
            )}
            {communication.isConsentForAiCalls != null && (
              <ConsentTag isConsent={communication.isConsentForAiCalls}>AI Calls</ConsentTag>
            )}
            {!!communication.isCeaseCommunication && (
              <StyledTag color="red">Cease Communication</StyledTag>
            )}
          </div>
        );
      },
    },
    {
      title: "Call Recording",
      key: "callRecording",
      width: 250,
      render: (_, record) => {
        if (!record.callRecordingUrl || record.callRecordingUrl === "") {
          return "-";
        }
        return (
          <audio
            controls
            src={record.callRecordingUrl}
            style={{ width: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <track default kind="captions" srcLang="en" />
          </audio>
        );
      },
    },
    {
      title: "External Call ID",
      dataIndex: "externalCallId",
      key: "externalCallId",
      width: 200,
      render: (externalCallId, record) => {
        if (!externalCallId) return "-";
        if (record.contactMethod === "ai_dialer") {
          return (
            <a
              href={`https://app.bland.ai/dashboard/call-logs/${externalCallId}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {externalCallId}
            </a>
          );
        }
        return externalCallId;
      },
      hidden: !me?.isStaff,
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      fixed: "right",
      width: 400,
      render: (text, record) => {
        const hasCallRecording = record.callRecordingUrl && record.callRecordingUrl !== "";
        const hasCallTranscription =
          record.callTranscription &&
          Array.isArray(record.callTranscription) &&
          record.callTranscription.length > 0;
        return (
          <StyledSpan>
            {text?.length > 200 || hasCallTranscription || hasCallRecording ? (
              <span>
                {text?.substring(0, 200)}...
                <StyledButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRecord(record);
                  }}
                  type="button"
                >
                  See Details
                </StyledButton>
              </span>
            ) : (
              text
            )}
          </StyledSpan>
        );
      },
      hidden: !isCommunicationGetNotesAuthorized,
    },
  ].filter((column) => !column.hidden);

  const tableColumns = useHistoriesEndpoint ? historiesColumns : taskColumns;

  const effectiveFilterParams = useMemo(() => {
    if (!useHistoriesEndpoint) return filterParams;
    const sourceFilters = filterParams?.filters ?? {};
    const {
      accountStatusesInclude,
      accountStatusesExclude,
      scheduledStartDate,
      scheduledEndDate,
      communicationMediums,
      ...rest
    } = sourceFilters;
    const mappedFilters = { ...rest };
    if (accountStatusesInclude !== undefined) {
      mappedFilters.workflowStateIds = accountStatusesInclude;
    }
    if (accountStatusesExclude !== undefined) {
      mappedFilters.excludeWorkflowStateIds = accountStatusesExclude;
    }
    if (scheduledStartDate !== undefined) {
      mappedFilters.communicationStartTimeStart = scheduledStartDate;
    }
    if (scheduledEndDate !== undefined) {
      mappedFilters.communicationStartTimeEnd = scheduledEndDate;
    }
    if (communicationMediums !== undefined) {
      mappedFilters.contactMethods = communicationMediums.map((medium) =>
        medium === "artificial_intelligence_phone_agent" ? "ai_dialer" : medium,
      );
    }
    return { ...filterParams, filters: mappedFilters };
  }, [filterParams, useHistoriesEndpoint]);

  const queryParams = {
    ...effectiveFilterParams,
    nextToken: pagination.nextToken,
    prevToken: pagination.prevToken,
    pageSize: pagination.pageSize,
    includeResultCount: false,
  };

  const {
    data: taskData,
    isLoading: isTasksLoading,
    isFetching: isTasksFetching,
  } = useGetCommunicationTasksListQuery(queryParams, { skip: useHistoriesEndpoint });

  const {
    data: historiesData,
    isLoading: isHistoriesLoading,
    isFetching: isHistoriesFetching,
  } = useGetCommunicationHistoriesListQuery(queryParams, { skip: !useHistoriesEndpoint });

  const communicationTasks = useHistoriesEndpoint ? historiesData : taskData;
  const isLoading = useHistoriesEndpoint ? isHistoriesLoading : isTasksLoading;
  const isFetching = useHistoriesEndpoint ? isHistoriesFetching : isTasksFetching;

  const handleTableChange = (newPagination, _, sorter) => {
    setPagination(newPagination);
  };

  const rowSelection = {
    onChange: (selectedRowKeys, newSelectedRows) => {
      setSelectedRows(newSelectedRows);
    },
  };

  // Needed because the actions column is fixed and virtually rendered so that pointer-events: none does not work
  const isActionsColumn = (element) => {
    return (
      element.classList.contains("ant-table-cell-fix-right") ||
      element.classList.contains("ant-dropdown-menu-item") ||
      element.classList.contains("ant-popover") ||
      element.classList.contains("ant-popconfirm") ||
      element.nextSibling === null ||
      element.tagName === "svg" ||
      element.tagName === "path"
    );
  };

  return (
    <StyledCol>
      {useHistoriesEndpoint && (
        <NotesModal
          title="Call Details"
          open={selectedRecord !== null}
          onClose={() => setSelectedRecord(null)}
          record={selectedRecord}
        />
      )}
      {!useHistoriesEndpoint && (
        <StyledActionContainer>
          <StyledActions>
            {/* Add button to delete */}
            <CommunicationTasksBulkActions dataSource={selectedRows} />
          </StyledActions>
        </StyledActionContainer>
      )}
      <StyledTable
        $hideActionContainer={useHistoriesEndpoint}
        bordered
        sticky
        scroll={{ x: "max-content" }}
        rowSelection={
          useHistoriesEndpoint
            ? undefined
            : {
                type: "checkbox",
                ...rowSelection,
              }
        }
        pageSize={pagination.pageSize}
        prevToken={communicationTasks?.meta?.prevToken}
        nextToken={communicationTasks?.meta?.nextToken}
        resultCount={communicationTasks?.meta?.resultCount}
        virtualPage={pagination.virtualPage}
        setVirtualPage={(page) => setPagination((prev) => ({ ...prev, virtualPage: page }))}
        columns={tableColumns}
        dataSource={communicationTasks?.results}
        loading={isLoading || isFetching}
        onRow={(record) => ({
          onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
            // allow users to select text so it does not trigger a click into the row
            const selection = window.getSelection().toString();
            if (selection.length <= 0 && !isLoading && !isActionsColumn(e.target)) {
              gotoDebtorProfile(record);
            }
          },
        })}
        onChange={handleTableChange}
      />
    </StyledCol>
  );
}
export default CommunicationTasksTable;
